import { Button, ColorPicker, Form, message, Spin } from "antd";
import { FC, useEffect, useState } from "react";
import StepHeading from "./StepHeading";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UploadFile } from "antd/lib";
import { useSelector } from "react-redux";
import {
  getOnlinePresence,
  updateOnlinePresence,
} from "../../../network/onlineBooking";
import { useIntl } from "react-intl";
import IMGUploader from "../../common/ui/IMGUploader";

interface OnlinePresenceProps {}

const OnlinePresence: FC<OnlinePresenceProps> = ({}) => {
  const { locationId } = useSelector(
    ({ calendar }: { calendar: { locationId: number } }) => calendar
  );

  const [color, setColor] = useState("#fff");
  const [backgroundColor, setBackGroundColor] = useState("#00086d");
  const [form] = Form.useForm();

  const [logoImg, setLogoImg] = useState<UploadFile[]>([]);
  const [imgs, setImgs] = useState<UploadFile[]>([]);
  const {
    data: locationDetails,
    refetch,
    isLoading: fetchingLocationDetails,
  } = useQuery(
    ["onlinePresence", locationId],
    () => getOnlinePresence(locationId),
    {
      enabled: !!locationId,
      select: (res) => res?.data?.data,
    }
  );
  const initializeData = () => {
    if (locationDetails) {
      const colorsIndex = locationDetails?.findIndex(
        (item: any) => item?.key === "brand_color_theme"
      );
      const logoIndex = locationDetails?.findIndex(
        (item: any) => item?.key === "brand_logo"
      );
      const imagesIndex = locationDetails?.findIndex(
        (item: any) => item?.key === "brand_gallery"
      );
      if (colorsIndex >= 0) {
        setColor(locationDetails[colorsIndex]?.value?.primary_color || "#fff");
        setBackGroundColor(
          locationDetails[colorsIndex]?.value?.background_color || "#00086d"
        );
        form.setFieldsValue({
          backgroundColor:
            locationDetails[colorsIndex]?.value?.background_color || "#fff",
          color:
            locationDetails[colorsIndex]?.value?.primary_color || "#00086d",
        });
      }
      if (logoIndex >= 0) {
        if (
          typeof locationDetails[logoIndex]?.media[0]?.media_url === "string" &&
          locationDetails[logoIndex]?.media[0]?.media_url?.length > 0
        ) {
          setLogoImg([
            {
              uid: locationDetails[logoIndex]?.media[0]?.id,
              name: locationDetails[logoIndex]?.media[0]?.name,
              url: locationDetails[logoIndex]?.media[0]?.media_url,
            },
          ]);
        }
      }
      if (imagesIndex >= 0) {
        setImgs(() =>
          locationDetails[imagesIndex]?.media.map((img: any) => {
            const file = {
              uid: img?.uuid,
              name: img?.name,
              url: img?.media_url,
            };
            return file;
          })
        );
      }
    }
  };
  useEffect(() => {
    initializeData();
  }, [locationDetails]);

  const { mutate, isLoading } = useMutation(
    (formData: FormData) => updateOnlinePresence(locationId, formData),
    {
      onSuccess: (res: any) => {
        refetch();
        if (res?.response?.status === 422 || res?.response?.status === 500) {
          message.error(res?.response?.data?.message);
        } else {
          message.success("تم تحديث الانطباع الاول  بنجاح");
        }
      },
    }
  );
  const handleSubmit = () => {
    if (!locationId) {
      message.error("يجب تحديد الموقع");
      return;
    }
    if (!imgs.length || !logoImg.length) {
      message.error("يجب تحميل الشعار والصور");
      return;
    }
    const formData = new FormData();
    formData.append("brand_color_theme[primary_color]", color);
    formData.append("brand_color_theme[background_color]", backgroundColor);
    if (logoImg[0]?.originFileObj) {
      formData.append("logo", logoImg[0].originFileObj);
    }
    if (imgs) {
      imgs.forEach((img, index) => {
        if (img.originFileObj) {
          formData.append(`gallery[${index}]`, img.originFileObj);
        }
      });
    }
    const imagesIndex = locationDetails?.findIndex(
      (item: any) => item?.key === "brand_gallery"
    );
    if (imagesIndex) {
      const imgsUids =
        locationDetails[imagesIndex]?.media?.map((img: any) => {
          if (img?.uuid) {
            return img?.uuid;
          }
        }) || [];

      imgs.forEach((img, i) => {
        if (!img.originFileObj) {
          if (img.uid) {
            const index = imgsUids.indexOf(img.uid);
            if (index > -1) {
              imgsUids.splice(index, 1);
            }
          }
        }
      });

      imgsUids.forEach((img: any, i: number) => {
        formData.append(`gallery_deleted_ids[${i}]`, img as string);
      });
    }

    mutate(formData);
  };
  const handleCancel = () => {
    initializeData();
  };
  const intl = useIntl();

  return (
    <Spin spinning={fetchingLocationDetails}>
      <Form form={form}>
        <div className="py-12 px-8 bg-[#EEF0FF] space-y-3">
          <div className="space-y-3">
            <h2 className="text-2xl text-color-primary">
              {intl.formatMessage({ id: "first_impression_title" })}
            </h2>
            <p className="text-[#75757E]">
              {intl.formatMessage({ id: "first_impression_description" })}
            </p>
          </div>
          <div className="grid grid-col-1 lg:grid-cols-8  gap-6 w-full">
            <div className="bg-white col-span-1 lg:col-span-2 rounded py-8 px-2 ">
              <StepHeading
                step={1}
                title={intl.formatMessage({ id: "branding_title" })}
                description={intl.formatMessage({ id: "branding_description" })}
              />
              <div className="text-center">
                <div className="my-4 ">
                  <Form.Item name={"logo"}>
                    <IMGUploader
                      imgValidation
                      fileList={logoImg}
                      setFileList={setLogoImg}
                    />
                  </Form.Item>
                </div>
                <p className="text-[14px] font-semibold text-color-sec-title">
                  {intl.formatMessage({ id: "upload_logo_title" })}
                </p>
              </div>
            </div>
            <div className="bg-white col-span-1 lg:col-span-3 rounded p-8 space-y-4">
              <StepHeading
                step={2}
                title={intl.formatMessage({ id: "button_color_title" })}
                description={intl.formatMessage({
                  id: "button_color_description",
                })}
              />
              <div
                dir="ltr"
                className=" flex flex-col gap-2 justify-center items-center"
              >
                <Form.Item
                  name={"backgroundColor"}
                  initialValue={backgroundColor}
                >
                  <ColorPicker
                    value={backgroundColor}
                    onChange={(color) => {
                      setBackGroundColor(color.toHexString());
                    }}
                    className="items-center px-2"
                    showText={(colorObj) => (
                      <div className="text-[14px] min-w-[180px] whitespace-nowrap p-2   flex items-center justify-between gap-4">
                        <span className="">
                          {intl.formatMessage({ id: "background_color" })}
                        </span>
                        <span>
                          {backgroundColor ||
                            intl.formatMessage({ id: "choose_color" })}
                        </span>
                      </div>
                    )}
                  />
                </Form.Item>

                <Form.Item name={"color"} initialValue={color}>
                  <ColorPicker
                    value={color}
                    className="items-center px-2"
                    onChange={(color) => {
                      setColor(color.toHexString());
                    }}
                    showText={(colorObj) => (
                      <div className="text-[14px] min-w-[180px] p-2 flex items-center justify-between gap-4">
                        <span>{intl.formatMessage({ id: "font_color" })}</span>
                        <span>
                          {color || intl.formatMessage({ id: "choose_color" })}
                        </span>
                      </div>
                    )}
                  />
                </Form.Item>
                <div className="text-center">
                  <Button
                    type="text"
                    htmlType="button"
                    className=" w-[175px] h-[42px]  font-semibold"
                    style={{
                      backgroundColor: backgroundColor,
                      color: color,
                    }}
                  >
                    {intl.formatMessage({ id: "book_now" })}
                  </Button>
                </div>
              </div>
            </div>
            <div className="bg-white col-span-1 lg:col-span-3 rounded p-8 overflow-y-hidden">
              <StepHeading
                step={3}
                title={intl.formatMessage({ id: "salon_images_title" })}
                description={intl.formatMessage({
                  id: "salon_images_description",
                })}
              />
              {/* <div className="  upload-img-border multi-imgs-upload bg-[#EEF0FF] my-4 p-2 rounded text-center "> */}
              <div className="mt-6">
                <Form.Item name={"imgs"}>
                  {/* <UploadSalonImages
                    imagesCount={7}
                    imgs={imgs}
                    setImgs={setImgs}
                  /> */}
                  <IMGUploader
                    maxCount={6}
                    fileList={imgs}
                    setFileList={setImgs}
                    listType="picture-card"
                    imgValidation
                    name="imgs"
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end ">
            <Button
              type="primary"
              htmlType="button"
              className="w-full  md:w-[165px] m-1"
              onClick={handleSubmit}
              disabled={
                !form.isFieldsTouched() && !logoImg?.length && !imgs?.length
              }
              loading={isLoading}
            >
              {intl.formatMessage({ id: "save" })}
            </Button>
            <Button
              type="default"
              htmlType="button"
              className="w-full md:w-[165px] m-1"
              onClick={handleCancel}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
          </div>
        </div>
      </Form>
    </Spin>
  );
};

export default OnlinePresence;
